/**
 * Admin · Routing rules (§21, §8.3/§8.5). ADMIN sees a full editor — create /
 * edit / delete / reorder rules and a dry-run tester (§8.5). MANAGER sees the
 * read-only table. Writes go through the ADMIN API (audited, optimistic
 * concurrency); "first matching rule by priority wins".
 */
import { useCallback, useMemo, useState } from "react";
import { ApiError, api, type AdminRoutingRule } from "../../lib/api.js";
import { useResource } from "../../lib/useResource.js";
import { useSession } from "../../components/auth.js";
import { describeCondition } from "../../lib/conditions.js";
import { humanizeServiceCode } from "../../lib/format.js";
import {
  Badge,
  StateChip,
  CarrierMark,
  DataTable,
  type Column,
  Button,
  Icon,
  EmptyState,
  ErrorState,
  SkeletonRows,
  useToast,
} from "../../components/ui/index.js";
import { RuleEditor } from "./routing/RuleEditor.js";
import { RoutingTester } from "./routing/RoutingTester.js";

const READONLY_COLUMNS: Array<Column<AdminRoutingRule>> = [
  { key: "priority", header: "#", align: "right", cell: (r) => <span className="tnum cell-strong">{r.priority}</span> },
  {
    key: "rule",
    header: "Rule",
    cell: (r) => (
      <span className="cell-stack">
        <span className="cell-primary">{r.name}</span>
        <span className="cell-sub cell-mono">{describeCondition(r.condition)}</span>
      </span>
    ),
  },
  {
    key: "target",
    header: "→ Carrier / Service",
    cell: (r) => (
      <span className="q-carrier">
        <CarrierMark code={r.carrierCode} showName />
        <span className="cell-sub">{humanizeServiceCode(r.serviceCode)}</span>
      </span>
    ),
  },
  { key: "scope", header: "Scope", cell: (r) => (r.storeId ? <Badge>store</Badge> : <span className="muted">all stores</span>) },
  { key: "enabled", header: "Status", cell: (r) => <StateChip on={r.enabled} /> },
];

export function Routing() {
  const { user } = useSession();
  const isAdmin = user?.role === "ADMIN";
  const r = useResource(api.admin.routingRules);
  const carriersRes = useResource(api.admin.carriers);
  const storesRes = useResource(api.admin.stores);
  const { toast } = useToast();

  const [editing, setEditing] = useState<AdminRoutingRule | null | "new">(null);
  const [tester, setTester] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = r.reload;
  const carrierCodes = (carriersRes.data?.carriers ?? []).map((c) => c.code);
  const stores = (storesRes.data?.stores ?? []).map((s) => ({ id: s.id, label: `${s.name} (${s.region})` }));

  const rules = useMemo(() => r.data?.rules ?? [], [r.data]);

  const move = useCallback(
    async (index: number, dir: -1 | 1) => {
      const next = index + dir;
      if (next < 0 || next >= rules.length) return;
      const order = rules.map((x) => x.id);
      [order[index], order[next]] = [order[next]!, order[index]!];
      setBusy(true);
      try {
        await api.admin.reorderRoutingRules(order);
        reload();
      } catch {
        toast("Couldn’t reorder rules.", "danger");
      } finally {
        setBusy(false);
      }
    },
    [rules, reload, toast],
  );

  const toggle = useCallback(
    async (rule: AdminRoutingRule) => {
      setBusy(true);
      try {
        await api.admin.updateRoutingRule(
          rule.id,
          {
            name: rule.name,
            priority: rule.priority,
            condition: rule.condition,
            carrierCode: rule.carrierCode,
            serviceCode: rule.serviceCode,
            enabled: !rule.enabled,
            storeId: rule.storeId,
          },
          rule.updatedAt,
        );
        reload();
      } catch (e) {
        toast(e instanceof ApiError && e.status === 409 ? "Rule changed elsewhere — reloading." : "Couldn’t update the rule.", "danger");
        reload();
      } finally {
        setBusy(false);
      }
    },
    [reload, toast],
  );

  const remove = useCallback(
    async (rule: AdminRoutingRule) => {
      if (!window.confirm(`Delete routing rule “${rule.name}”? Orders it would have matched fall through to the next rule.`)) return;
      setBusy(true);
      try {
        await api.admin.deleteRoutingRule(rule.id);
        toast("Rule deleted", "success");
        reload();
      } catch {
        toast("Couldn’t delete the rule.", "danger");
      } finally {
        setBusy(false);
      }
    },
    [reload, toast],
  );

  // ---- read-only view (MANAGER) ----
  if (!isAdmin) {
    return (
      <section className="admin">
        <div className="page-head">
          <div className="page-head__title">
            <h1>Routing rules</h1>
            {r.data && <span className="page-head__count">{r.data.rules.length} rules</span>}
          </div>
          <span className="soon" title="Admin role required to edit">Read-only · admin can edit</span>
        </div>
        {r.error ? (
          <ErrorState title="Couldn’t load this" hint="Retry in a moment." onRetry={r.reload} />
        ) : r.loading && !r.data ? (
          <div className="table-card"><SkeletonRows rows={6} cols={5} /></div>
        ) : rules.length === 0 ? (
          <EmptyState icon="route" title="No routing rules" hint="An admin can add the §8.3 ruleset." />
        ) : (
          <DataTable ariaLabel="Routing rules" rows={rules} getRowKey={(x) => x.id} columns={READONLY_COLUMNS} />
        )}
      </section>
    );
  }

  // ---- editor view (ADMIN) ----
  return (
    <section className="admin">
      <div className="page-head">
        <div className="page-head__title">
          <h1>Routing rules</h1>
          {r.data && <span className="page-head__count">{rules.length} rules · first match wins</span>}
        </div>
        <div className="page-head__actions">
          <Button variant="secondary" size="sm" icon="bolt" onClick={() => setTester((t) => !t)} aria-expanded={tester}>
            Test routing
          </Button>
          <Button variant="primary" size="sm" icon="plus" onClick={() => setEditing("new")}>
            New rule
          </Button>
        </div>
      </div>

      {tester && <RoutingTester rules={rules} onClose={() => setTester(false)} />}

      {r.error ? (
        <ErrorState title="Couldn’t load this" hint="Retry in a moment." onRetry={r.reload} />
      ) : r.loading && !r.data ? (
        <div className="table-card"><SkeletonRows rows={6} cols={5} /></div>
      ) : rules.length === 0 ? (
        <EmptyState icon="route" title="No routing rules" hint="Add your first rule — orders with no match surface as UNROUTABLE." />
      ) : (
        <ul className="rule-list" aria-busy={busy}>
          {rules.map((rule, i) => (
            <li key={rule.id} className={"rule-row" + (rule.enabled ? "" : " rule-row--off")}>
              <div className="rule-row__rank">
                <span className="tnum cell-strong">{rule.priority}</span>
                <div className="rule-row__moves">
                  <button type="button" aria-label="Move up" disabled={i === 0 || busy} onClick={() => void move(i, -1)}>
                    <Icon name="chevronUp" size={14} />
                  </button>
                  <button type="button" aria-label="Move down" disabled={i === rules.length - 1 || busy} onClick={() => void move(i, 1)}>
                    <Icon name="chevronDown" size={14} />
                  </button>
                </div>
              </div>
              <div className="rule-row__body">
                <span className="rule-row__name">{rule.name}</span>
                <span className="cell-sub cell-mono">{describeCondition(rule.condition)}</span>
              </div>
              <div className="rule-row__target">
                <CarrierMark code={rule.carrierCode} showName />
                <span className="cell-sub">{humanizeServiceCode(rule.serviceCode)}</span>
                {rule.storeId ? <span className="cell-sub">· store-scoped</span> : null}
              </div>
              <div className="rule-row__actions">
                <button
                  type="button"
                  className="rule-row__toggle"
                  aria-pressed={rule.enabled}
                  title={rule.enabled ? "Disable" : "Enable"}
                  disabled={busy}
                  onClick={() => void toggle(rule)}
                >
                  <StateChip on={rule.enabled} />
                </button>
                <Button variant="ghost" size="sm" icon="edit" iconOnly aria-label="Edit rule" onClick={() => setEditing(rule)} />
                <Button variant="ghost" size="sm" icon="x" iconOnly aria-label="Delete rule" onClick={() => void remove(rule)} />
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <RuleEditor
          rule={editing === "new" ? null : editing}
          carrierCodes={carrierCodes}
          stores={stores}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </section>
  );
}
